import React from 'react';
import { ActionBar, Button, Tooltip } from '@onward-delivery/design';

const PanelButtons = ({ buttons, hidden = false }) => (
    <div
        className={`flex flex-wrap items-center justify-center gap-2 [grid-area:1/1] ${hidden ? 'invisible' : ''}`}
        aria-hidden={hidden || undefined}
    >
        {buttons.map((button) => {
            const rendered = (
                <Button
                    key={button.key}
                    variant={button.variant}
                    size="sm"
                    disabled={button.disabled}
                    onClick={button.onClick}
                >
                    {button.label}
                </Button>
            );
            // A disabled button fires no pointer events, so the tooltip hangs off a wrapper.
            return button.reason && !hidden ? (
                <Tooltip key={button.key} content={button.reason}>
                    <span>{rendered}</span>
                </Tooltip>
            ) : (
                rendered
            );
        })}
    </div>
);

/**
 * The floating options panel for checked cards: the count on the left, the action buttons centered, Clear on the
 * right. An invisible copy of every action button shares the middle grid cell, so the panel is always as wide as the
 * full set and doesn't resize as the checked cards change which buttons show.
 *
 * @param {{ count: number, buttons: object[], onClear: Function }} props `buttons` = `{ key, label, variant, show,
 *   disabled, reason, onClick, sizes }`. `reason` is a tooltip, for a disabled button or one that skips some rows.
 *   `sizes: false` leaves a button out of the width, for one that never shows alongside a wider one.
 */
const SelectionPanel = ({ count, buttons, onClear }) => (
    <ActionBar
        className="bottom-4 mx-auto mt-4 w-fit max-w-full rounded-xl border border-border shadow-[0_4px_16px_rgba(15,23,42,0.08)] [&>[data-slot=action-bar-left]]:flex-1"
        left={
            <div className="grid w-full grid-cols-[1fr_auto_1fr] items-center gap-4 text-[13px]">
                <div className="flex items-center gap-3 justify-self-start">
                    <span className="whitespace-nowrap">
                        <b>{count}</b> selected
                    </span>
                    <span className="h-5 w-px bg-border" aria-hidden="true" />
                </div>
                <div className="grid">
                    <PanelButtons buttons={buttons.filter((b) => b.sizes !== false)} hidden />
                    <PanelButtons buttons={buttons.filter((b) => b.show)} />
                </div>
                <Button variant="ghost" size="sm" className="justify-self-end" onClick={onClear}>
                    Clear
                </Button>
            </div>
        }
    />
);

export default SelectionPanel;
